'use client';

import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import Link from 'next/link';
import styles from '../LoginForm/LoginForm.module.css';
import { requestPasswordResetSchema } from './RequestPasswordResetForm.schema';
import type { FeedbackMessageBannerProps } from '../FeedbackMessageBanner/FeedbackMessageBanner.types';
import { AuthenticationIntro } from '../AuthenticationIntro/AuthenticationIntro';
import { Button } from '../../components/Button/Button';
import { Input } from '../../components/Input/Input';

export type RequestPasswordResetFormValues = z.infer<typeof requestPasswordResetSchema>;

export type RequestPasswordResetFormProps = Readonly<{
  onSubmit: (data: RequestPasswordResetFormValues) => void | Promise<void>;
  banner?: Pick<FeedbackMessageBannerProps, 'variant' | 'message'>;
}>;

export function RequestPasswordResetForm({ onSubmit, banner }: RequestPasswordResetFormProps) {
  const { control, handleSubmit } = useForm<RequestPasswordResetFormValues>({
    resolver: zodResolver(requestPasswordResetSchema),
    defaultValues: { email: '' },
  });

  const defaultBanner = useMemo<Pick<FeedbackMessageBannerProps, 'variant' | 'message'>>(
    () => ({
      variant: 'info',
      message: "Enter your email address and we'll send you a link to reset your password.",
    }),
    []
  );
  const effectiveBanner = banner ?? defaultBanner;

  return (
    <div className="bg-white p-lr-x3 p-tb-1-75 rounded-3xl w-full grid justify-items-center text-center gap-6">
      <AuthenticationIntro
        title="Reset Password"
        subtitle="To Your Account"
        banner={{ message: effectiveBanner.message, variant: effectiveBanner.variant }}
      />

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="grid gap-6 w-full text-left">
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Email *"
              placeholder="eg. user@domain.com"
              className={styles.input}
              leftIcon={<i className="clara-envelope-outline" aria-hidden="true" />}
              type="email"
              field={field}
              validationMessage={error?.message}
            />
          )}
        />
        <Button className={`${styles.signInButton} mb-[var(--h3)] justify-self-center`} type="submit">
          Reset Password
        </Button>
      </form>
      <Link href="/login" className="w-full flex justify-center p-b-1-5" aria-label="Back to sign in">
        <Button styleType="outline" variant="primary">
          <i className="clara-chevron-left-outline" aria-hidden="true" />
          Back To Sign In
        </Button>
      </Link>
    </div>
  );
}
